import { forwardRef, ReactNode, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useVariant } from "@~/contexts/VariantContext/VariantContext";
import { isM2MFabricProduct } from "./components/M2MProductSelector/utils";
import { M2MProductSelector } from "./components/M2MProductSelector/M2MProductSelector";
import { Product } from "../../../types/types";
import styles from "./ProductLink.module.scss";
import { getProductUrl, isEligibleForQuickView } from "@~/utils/product";
import { useProductCardEvent } from "@~/contexts/ProductCardEventContext/ProductCardEventContext.tsx";
import { productCardEvents } from "@~/contexts/ProductCardEventContext/productCardEvents";
import { QuickViewModal } from "../QuickViewModal/QuickViewModal";

type ProductLinkProps = {
  product: Product;
  children: ReactNode;
  className?: string;
  componentName?: string;
  tabIndex?: number;
  testId?: string;
  ariaHidden?: boolean;
  ariaLabel?: string;
  noFollow?: boolean;
  opensQuickView?: boolean;
  quickViewConfig?: string;
};

export const ProductLink = forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  ProductLinkProps
>(
  (
    {
      product,
      children,
      className,
      componentName,
      tabIndex,
      testId,
      ariaHidden,
      ariaLabel,
      noFollow = false,
      opensQuickView: opensQuickViewProp = false,
      quickViewConfig,
    },
    ref,
  ) => {
    const [showM2MModal, setShowM2MModal] = useState(false);
    const [showQV, setShowQV] = useState(false);
    const { variant } = useVariant();
    const isM2MProduct = isM2MFabricProduct(product);
    const portalRef = useRef(null);
    const dispatchCardEvent = useProductCardEvent();
    const opensQuickView =
      opensQuickViewProp && isEligibleForQuickView(product);

    const handleM2MClick = () => {
      window.dispatchEvent(new CustomEvent("closeAllM2MModals"));
      setShowM2MModal(true);
    };

    const handleCloseModal = () => {
      setShowM2MModal(false);
    };

    useEffect(function subscribeToM2MModalCloseEvents() {
      const handleM2MModalClose = () => setShowM2MModal(false);
      window.addEventListener("closeAllM2MModals", handleM2MModalClose);

      return function unsubscribeFromM2MModalCloseEvents() {
        window.removeEventListener("closeAllM2MModals", handleM2MModalClose);
      };
    }, []);

    if (isM2MProduct) {
      return (
        <>
          <div ref={portalRef} className="m2mModalPortalReference" />
          <button
            ref={ref as React.Ref<HTMLButtonElement>}
            className={classNames(styles.container, className)}
            onClick={handleM2MClick}
            data-component={componentName ? `pcl-${componentName}` : undefined}
            type="button"
            tabIndex={tabIndex}
            aria-haspopup="dialog"
            {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
          >
            {children}
          </button>
          {showM2MModal && (
            <M2MProductSelector
              isOpen={showM2MModal}
              onClose={handleCloseModal}
              pdpUrl={variant?.pdpURL.url || ""}
            />
          )}
        </>
      );
    }

    if (opensQuickView) {
      return (
        <>
          <button
            ref={ref as React.Ref<HTMLButtonElement>}
            className={classNames(styles.container, className)}
            data-component={componentName ? `pcl-${componentName}` : undefined}
            data-testid={testId ?? undefined}
            type="button"
            tabIndex={tabIndex}
            aria-hidden={ariaHidden}
            {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
            onClick={() => {
              dispatchCardEvent(productCardEvents.cardClick);
              setShowQV(true);
            }}
          >
            {children}
          </button>
          {showQV && (
            <QuickViewModal
              product={product}
              showModal={showQV}
              onClose={() => setShowQV(false)}
              originator="PRODUCT_CARD"
              config={quickViewConfig}
            />
          )}
        </>
      );
    }

    return (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={getProductUrl(product, variant)}
        className={classNames(styles.container, className)}
        data-component={componentName ? `pcl-${componentName}` : undefined}
        data-testid={testId ?? undefined}
        tabIndex={tabIndex}
        aria-hidden={ariaHidden}
        aria-label={ariaLabel}
        {...(noFollow && { rel: "nofollow" })}
        onClick={() => dispatchCardEvent(productCardEvents.cardClick)}
      >
        {children}
      </a>
    );
  },
);

ProductLink.displayName = "ProductLink";
